import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { highlightHtmlContent } from "../../utils/htmlUtils";

const CollapsedStackView = ({
  job,
  searchTerm,
  hasActiveFilters,
  openAiDialogForJob,
  toggleStackExpansion,
  onOpenEdit,
  onCreateTask,
  onOpenSlideshow,
  onFilterByProject,
  onFilterByWorkspace,
  jobUsers,
  loadingUsersByJob,
  workspaceRole,
  onDenyUserClick,
  fetchJobPermissionUsers,
}) => {


  const [showUsersDropdown, setShowUsersDropdown] = useState(false);
  const [hasFetchedUsers, setHasFetchedUsers] = useState(false);
  const [dropdownDirection, setDropdownDirection] = useState("down");
  const dropdownRef = useRef(null);
  const iconRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        iconRef.current &&
        !iconRef.current.contains(event.target)
      ) {
        setShowUsersDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!job) return null;

  const handleProjectClick = (e, projectId, projectName) => {
    e.stopPropagation();
    if (projectId && onFilterByProject) {
      onFilterByProject(projectId, projectName);
    }
  };

  const handleWorkspaceClick = (e, workspaceId, workspaceName) => {
    e.stopPropagation();
    if (workspaceId && onFilterByWorkspace) {
      onFilterByWorkspace(workspaceId, workspaceName);
    }
  };

  const handleIconClick = async (e) => {
    e.stopPropagation();

    if (iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownHeight = 300;

      if (spaceBelow < dropdownHeight) {
        setDropdownDirection("up");
      } else {
        setDropdownDirection("down");
      }
    }

    setShowUsersDropdown(!showUsersDropdown);

    if (
      !hasFetchedUsers &&
      !loadingUsersByJob[job.jobId] &&
      !jobUsers[job.jobId] &&
      fetchJobPermissionUsers
    ) {
      try {
        await fetchJobPermissionUsers(job.jobId);
        setHasFetchedUsers(true);
      } catch (error) {
        console.error("Error fetching users:", error);
        toast.error("Failed to load users");
      }
    }
  };

  const handleDenyClick = (e, job, workspaceId, userId) => {
    e.stopPropagation();
    setShowUsersDropdown(false);
    if (onDenyUserClick) {
      onDenyUserClick(job, workspaceId, userId);
    }
  };

  const jobName = job.jobName;
  const noteCount = job.noteCount || 0;
  const isLoading = job.isLoadingNotes;

  const workspaceName =
    job.workspaceName ||
    job.workspace?.name ||
    job.workspace?.workspaceName ||
    job.originalWorkspaceName ||
    (job.notes && job.notes[0]?.workspace) ||
    null;

  const projectName =
    job.projectName ||
    job.project?.name ||
    job.project?.projectName ||
    job.originalProjectName ||
    (job.notes && job.notes[0]?.project) ||
    null;

  const workspaceId =
    job.workspaceId ||
    job.workspace?.id ||
    job.defWorkId ||
    job.originalWorkspaceId;

  const projectId = job.projectId || job.project?.id || job.originalProjectId;

  const users = jobUsers[job.jobId] || [];
  const uniqueUsers = Array.from(
    new Map(users.map((user) => [user.id, user])).values(),
  );
  const isLoadingUsers = loadingUsersByJob[job.jobId];

  const isDenied = workspaceRole === 1;

  const getDropdownStyle = () => {
    if (iconRef.current && showUsersDropdown) {
      const rect = iconRef.current.getBoundingClientRect();

      if (dropdownDirection === "up") {
        return {
          position: "fixed",
          bottom: window.innerHeight - rect.top + 5,
          left: rect.left,
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
          minWidth: "220px",
          maxWidth: "260px",
          zIndex: 999999,
          border: "1px solid #e9ecef",
          overflow: "hidden",
        };
      } else {
        return {
          position: "fixed",
          top: rect.bottom + 5,
          left: rect.left,
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
          minWidth: "220px",
          maxWidth: "260px",
          zIndex: 999999,
          border: "1px solid #e9ecef",
          overflow: "hidden",
        };
      }
    }
    return { display: "none" };
  };

  return (
    <div
      key={`stack-${job.jobId || job.jobName}`}
      className={`collapsed-stack ${isLoading ? "loading" : ""}`}
      style={{
        cursor: "pointer",
        position: "relative",
        height: "280px",
        width: "100%",
      }}
    >
      {isLoading && (
        <div
          className="stack-loading-overlay"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
            borderRadius: "8px",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <i
              className="fas fa-spinner fa-spin"
              style={{
                fontSize: "24px",
                color: "#3498db",
                marginBottom: "10px",
              }}
            />
            <div style={{ fontSize: "14px", color: "#666" }}>Loading...</div>
          </div>
        </div>
      )}

      {[...Array(Math.min(noteCount, 5))].map((_, index) => {
        const isTopCard = index === 0;
        return (
          <div
            key={`layer-${index}`}
            className="stack-layer-card"
            style={{
              position: "absolute",
              right: "0",
              left: `${index * 15}px`,
              transform: `rotate(${index * -2}deg)`,
              zIndex: 5 - index,
              width: "300px",
              height: "250px",
              opacity: 1 - index * 0.2,
              transition: "all 0.3s ease",
              overflow: "hidden",
            }}
          >
            {isTopCard ? (
              <div
                style={{
                  padding: "15px",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: "10px",
                    paddingBottom: "10px",
                    borderBottom: "1px solid #f0f0f0",
                    flexShrink: 0,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "5px",
                      }}
                    >
                      <i
                        className="fas fa-briefcase"
                        style={{ color: "#14A2B6", flexShrink: 0 }}
                      />
                      <div
                        style={{
                          fontWeight: 600,
                          color: "#2c3e50",
                          fontSize: "16px",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          gap: "2px",
                          minWidth: 0,
                          width: "100%",
                        }}
                      >
                        <span
                          style={{
                            lineHeight: 1,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "160px",
                            display: "block",
                          }}
                          title={job.jobName}
                        >
                          {job.jobName}
                        </span>
                        <div
                          style={{
                            display: "flex",
                            gap: "2px",
                          }}
                        >
                          {workspaceName && (
                            <span
                              onClick={(e) =>
                                handleWorkspaceClick(
                                  e,
                                  workspaceId,
                                  workspaceName,
                                )
                              }
                              title={workspaceName}
                              style={{
                                fontSize: "12px",
                                color: "#7f8c8d",
                                fontWeight: 400,
                                marginTop: "2px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: "140px",
                                display: "block",
                                cursor: "pointer",
                              }}
                            >
                              {workspaceName} /
                            </span>
                          )}
                          {projectName && (
                            <span
                              onClick={(e) =>
                                handleProjectClick(e, projectId, projectName)
                              }
                              title={projectName}
                              style={{
                                fontSize: "12px",
                                color: "#7f8c8d",
                                fontWeight: 400,
                                marginTop: "2px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: "140px",
                                display: "block",
                                cursor: "pointer",
                              }}
                            >
                              {projectName}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        background: "#14A2B6",
                        color: "white",
                        padding: "4px 10px",
                        borderRadius: "20px",
                        fontSize: "12px",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                      }}
                    >
                      <i className="fas fa-layer-group" />
                      <span>{noteCount} notes</span>
                    </div>
                  </div>
                </div>

                <div
                  onClick={() => toggleStackExpansion(jobName, job.jobId)}
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    minHeight: 0,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{
                        width: "28px",
                        height: "28px",
                        borderRadius: "50%",
                        backgroundColor: "#14A2B6",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        fontWeight: "bold",
                        flexShrink: 0,
                      }}
                    >
                      {job.lastSiteNoteUserName
                        ? job.lastSiteNoteUserName.charAt(0).toUpperCase()
                        : job.jobName?.charAt(0)?.toUpperCase() || "J"}
                    </div>
                    <span
                      style={{ fontSize: "14px", color: "#555", flexShrink: 0 }}
                    >
                      {job.lastSiteNoteUserName ||
                        (job.notes &&
                          job.notes.length > 0 &&
                          job.notes[0].userName) ||
                        "Loading..."}
                    </span>
                  </div>

                  <div
                    style={{
                      fontSize: "13px",
                      color: "#666",
                      lineHeight: 1.4,
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      padding: "8px",
                      backgroundColor: "#f8f9fa",
                      borderRadius: "4px",
                      borderLeft: "3px solid #21f869ff",
                      marginTop: "8px",
                      position: "relative",
                      maxHeight: "70px",
                      flexShrink: 0,
                    }}
                  >
                    <div
                      className="note-text-preview"
                      style={{
                        fontSize: "12px",
                        color: "#666",
                        lineHeight: 1.4,
                        maxHeight: "50px",
                        overflow: "hidden",
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        wordBreak: "break-word",
                      }}
                      dangerouslySetInnerHTML={{
                        __html: highlightHtmlContent(
                          job.lastSiteNote
                            ? job.lastSiteNote
                            : job.notes && job.notes.length > 0
                              ? job.notes[0].note || "No note content"
                              : hasActiveFilters && job.hasLoadedNotes
                                ? "No notes match current filters"
                                : "Click to load",
                          searchTerm,
                        ),
                      }}
                    />
                  </div>

                  <div
                    style={{
                      fontSize: "11px",
                      color: "#888",
                      marginTop: "auto",
                      paddingTop: "8px",
                      borderTop: "1px dashed #e9ecef",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      justifyContent: "space-between",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        position: "relative",
                      }}
                    >
                      <div
                        ref={iconRef}
                        style={{
                          position: "relative",
                          display: "inline-block",
                        }}
                      >
                        <button
                          onClick={handleIconClick}
                          title="View permitted users"
                          style={{
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            color: "#3498db",
                            fontSize: "14px",
                            padding: "4px",
                            display: "flex",
                            alignItems: "center",
                            position: "relative",
                          }}
                        >
                          <i className="fas fa-user-shield" />
                        </button>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <button
                        className="attachment-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          openAiDialogForJob(job);
                        }}
                        title="Summarize notes (AI)"
                        style={{
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          color: "#1976d2",
                          fontSize: "14px",
                        }}
                      >
                        <i className="fas fa-comments" />
                      </button>

                      <button
                        className="create-task-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (typeof onCreateTask === "function")
                            onCreateTask(job);
                        }}
                        title="Create Task"
                        style={{
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          fontSize: "14px",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <i
                          className="fas fa-plus-circle"
                          style={{ color: "#28a745" }}
                        />
                      </button>

                      <button
                        className="attachment-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (typeof onOpenEdit === "function") onOpenEdit(job);
                        }}
                        title="Edit job"
                        style={{
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          color: "#444",
                          fontSize: "14px",
                        }}
                      >
                        <i className="fas fa-cog" />
                      </button>
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    textAlign: "center",
                    paddingTop: "10px",
                    borderTop: "1px dashed #e9ecef",
                    marginTop: "10px",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#7f8c8d",
                      fontStyle: "italic",
                    }}
                  >
                    {isLoading ? "Loading..." : "Click to expand"}
                    {hasActiveFilters && !isLoading && " (filtered view)"}
                  </div>
                </div>
              </div>
            ) : (
              <div
                style={{
                  height: "100%",
                  background:
                    "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#bdc3c7",
                }}
              >
                <i
                  className="fas fa-sticky-note"
                  style={{ fontSize: "24px" }}
                />
              </div>
            )}
          </div>
        );
      })}

      {showUsersDropdown && iconRef.current && (
        <div ref={dropdownRef} style={getDropdownStyle()}>
          <div
            style={{
              padding: "10px 12px",
              backgroundColor: "#f8f9fa",
              borderBottom: "1px solid #e9ecef",
              fontWeight: 600,
              fontSize: "12px",
              color: "#2c3e50",
            }}
          >
            <i
              className="fas fa-user-shield"
              style={{ marginRight: "6px", color: "#3498db" }}
            />
            Permitted Users ({uniqueUsers.length})
          </div>
          <div
            style={{
              maxHeight: "220px",
              overflowY: "auto",
              scrollbarWidth: "thin",
            }}
          >
            {isLoadingUsers ? (
              <div
                style={{ padding: "20px", textAlign: "center", color: "#999" }}
              >
                <i
                  className="fas fa-spinner fa-spin"
                  style={{ fontSize: "16px" }}
                />
                <div style={{ marginTop: "8px", fontSize: "11px" }}>
                  Loading users...
                </div>
              </div>
            ) : uniqueUsers.length > 0 ? (
              uniqueUsers.map((user) => (
                <div
                  key={user.id}
                  style={{
                    padding: "8px 12px",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    borderBottom: "1px solid #f0f0f0",
                    cursor: "default",
                    justifyContent: "space-between",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <div
                      style={{
                        width: "28px",
                        height: "28px",
                        borderRadius: "50%",
                        backgroundColor: user.color,
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "11px",
                        fontWeight: "bold",
                        flexShrink: 0,
                      }}
                    >
                      {user.initials}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: "13px",
                          fontWeight: 500,
                          color: "#2c3e50",
                        }}
                      >
                        {user.name}
                      </div>
                    </div>
                  </div>
                  {isDenied && (
                    <i
                      className="fas fa-ban"
                      style={{
                        color: "#dc3545",
                        fontSize: "12px",
                        flexShrink: 0,
                        marginLeft: "8px",
                        cursor: "pointer",
                      }}
                      title="Deny permission for this job"
                      onClick={(e) =>
                        handleDenyClick(e, job, workspaceId, user.id)
                      }
                    />
                  )}
                </div>
              ))
            ) : (
              <div
                style={{
                  padding: "20px",
                  textAlign: "center",
                  color: "#999",
                  fontSize: "12px",
                }}
              >
                No permitted users assigned
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CollapsedStackView;